import React from "react";
import { Table, Tag, Button, Tooltip, Empty } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import type { Production } from "../../../Types";
import { PAGE_SIZE } from "../../../Constant";

interface Props {
  data: Production[];
  total: number;
  loading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  onViewDetail: (record: Production) => void;
}

export const ProductionTable: React.FC<Props> = ({
  data,
  total,
  loading,
  page,
  onPageChange,
  onViewDetail,
}) => {
  const columns: ColumnsType<Production> = [
    {
      title: "Date",
      dataIndex: "production_date",
      width: 110,
      render: (val) => (
        <span className="font-mono text-xs text-gray-500 font-medium">
          {dayjs(val).format("YYYY-MM-DD")}
        </span>
      ),
    },
    {
      title: "Product",
      key: "product",
      render: (_, r) => (
        <div className="flex flex-col gap-0.5">
          <span className="text-xs font-bold text-gray-700 leading-normal">
            {r.product_title}
          </span>
          <span className="text-[10px] text-gray-400 font-mono font-medium">
            {r.product_sku_code}
          </span>
        </div>
      ),
    },
    {
      title: "Batch",
      dataIndex: "product_batch_no",
      width: 130,
      render: (val) => (
        <Tag
          color="blue"
          className="font-mono text-[10px] font-bold uppercase tracking-wider"
        >
          {val}
        </Tag>
      ),
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      width: 100,
      align: "right",
      render: (val) => (
        <span className="font-mono text-xs font-bold text-teal-700">
          {Number(val).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      width: 140,
      align: "right",
      render: (val) => (
        <span className="font-mono text-xs font-bold text-gray-800">
          LKR{" "}
          {Number(val).toLocaleString("en-LK", { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 100,
      render: (val) => {
        const isCompleted = val?.toLowerCase() === "completed";
        return (
          <Tag
            color={isCompleted ? "success" : "warning"}
            className="text-[10px] uppercase font-bold"
          >
            {val || "Pending"}
          </Tag>
        );
      },
    },
    {
      title: "By",
      dataIndex: "creator_name",
      width: 130,
      render: (val) => (
        <span className="text-xs text-gray-500 font-medium">{val || "—"}</span>
      ),
    },
    {
      key: "actions",
      width: 50,
      align: "center",
      render: (_, record) => (
        <Tooltip title="View details" placement="topRight">
          <Button
            icon={<InfoCircleOutlined />}
            size="small"
            type="text"
            onClick={() => onViewDetail(record)}
            className="text-gray-400 hover:text-teal-600 transition-colors"
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Table
      dataSource={data}
      columns={columns}
      rowKey="id"
      loading={loading}
      size="small"
      pagination={{
        current: page,
        pageSize: PAGE_SIZE,
        total,
        onChange: onPageChange,
        showSizeChanger: false,
        showTotal: (totalRuns) => (
          <span className="text-xs text-gray-400 font-medium">
            Total runs: {totalRuns}
          </span>
        ),
        size: "small",
      }}
      scroll={{ x: 900 }}
      locale={{
        emptyText: (
          <Empty
            description="No production records logged yet."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ),
      }}
    />
  );
};
